import { useRef } from 'react';
import {
   QueryFunctionContext,
   QueryKey,
   useInfiniteQuery,
   UseInfiniteQueryOptions,
   UseInfiniteQueryResult,
} from '@tanstack/react-query';
import { ApiClient } from '@dozuki/web-js/utils';
import { ErrorMessages } from '~/monolith-api';
import { RegisteredQueryKeys } from './RegisteredQueryKeys';
import { useApiErrorMessageToast } from './useApiErrorMessageToast';
import { useRouter } from 'next/router';

/**
 * useIncrementQuery
 * @deprecated - Please use the version from ~/pages/_shared, and see ~/pages/Courses for an example of how to model queries and invalidation
 * @description - Used in place of 'useQuery' (or 'useInfiniteQuery') as a convenience method
 *    for loading additional results from a paginated set when the user requests additional
 *    data
 *
 * @usage -
 *  useIncrementalQuery(key: string, async (page: number) => {
      let { courses: data, hasMore } = await getCourses({
         ...options,
         page,
      });
      return { page, data, hasMore };
   });
 */

export type UseIncrementalQueryOptions<Model, KeyType extends QueryKey> = UseInfiniteQueryOptions<
   Incremental<Model>,
   any,
   Incremental<Model>,
   Incremental<Model>,
   KeyType
> & {
   firstPage: number;
   errorMessages?: ErrorMessages;
};

export type IncrementalQueryFunction<Model, KeyType extends QueryKey> = (
   page: number,
   ctx: QueryFunctionContext<KeyType>
) => Incremental<Model> | Promise<Incremental<Model>>;

export type UseIncrementalQueryResult<Model> = UseInfiniteQueryResult & {
   pageNumber: number | undefined;
   flattenedData: Model | undefined;
   total: number | undefined;
};

export function useIncrementalQuery<Model extends any[], ErrorType, VariableType>(
   // Expressing the full type definition here for better type hinting
   key: [RegisteredQueryKeys, ...any],
   queryFunction: IncrementalQueryFunction<Model, [RegisteredQueryKeys, ...any]>,
   options: UseIncrementalQueryOptions<Model, [RegisteredQueryKeys, ...any]> = { firstPage: 1 }
): UseIncrementalQueryResult<Model> {
   const { firstPage, ...infiniteQueryOptions } = options;
   const router = useRouter();

   const apiErrorMessageToast = useApiErrorMessageToast();

   const total = useRef<undefined | number>(undefined);

   // Wrap the queryFunction so that we can default the page, and inject it a little more intuitively.
   async function runQuery(queryContext: QueryFunctionContext<[RegisteredQueryKeys, ...any]>) {
      const page = queryContext.pageParam === undefined ? firstPage : queryContext.pageParam;
      const result = await queryFunction(page, queryContext);
      if (result.total !== undefined) total.current = result.total;
      return result;
   }

   const result = useInfiniteQuery(key, runQuery, {
      ...infiniteQueryOptions,
      keepPreviousData: true,
      getNextPageParam: ({ page, hasMore }) => (hasMore ? page + 1 : undefined),
      onError(error, ...args) {
         if (ApiClient.isInvalidLoginError(error)) {
            router.push('/Login');
         }
         if (!ApiClient.shouldRedirectError(error)) {
            apiErrorMessageToast.handle(error);
         }
         if (options.onError) {
            options.onError(error, ...args);
         }
      },
      useErrorBoundary: ApiClient.shouldRedirectError,
   });

   // Flatten the pages into a single result set for easier processing
   //   by the calling context
   const flattenedData: Model | undefined = result.data?.pages.reduce(
      (flattenedData, page) => [...flattenedData, ...page.data] as Model,
      [] as unknown as Model
   );

   return {
      ...result,
      pageNumber: result.data?.pages.length,
      total: total.current,
      flattenedData,
   };
}

type Incremental<Model> = {
   data: Model;
   page: number;
   hasMore: boolean;
   total?: number;
};
